'use client';

import type React from 'react';
import { useState } from 'react';
import Image from 'next/image';

import type { SanityImageDimensions } from '@skybeam/shared-sanity';
import { utils } from '../utils';

export type Item = {
  description: string;
  icon: React.ReactNode;
  iconUrl?: string;
  imageDimensions?: SanityImageDimensions | null;
  imageIcon?: React.ReactNode;
  imageSrc?: string;
  title: string;
};

interface ImageAccordionProps {
  items: Item[];
}

export const ImageAccordion: React.FC<ImageAccordionProps> = ({ items }) => {
  const [selectedItem, setSelectedItem] = useState(items[1]?.title);
  const selected = items.find((i) => i.title === selectedItem) || items[0]!;

  return (
    <>
      <div className="laptop:hidden flex flex-col gap-y-12">
        {items.map(
          ({
            description,
            icon,
            imageDimensions,
            imageIcon,
            imageSrc,
            title,
          }) => (
            <div key={title} className="flex flex-col items-center gap-y-4">
              <div className="flex size-8 items-center justify-center rounded-sm border border-blue-600 bg-blue-500 text-white [&_img]:brightness-0 [&_img]:invert">
                {icon}
              </div>
              <div className="font-proximaNova flex flex-col gap-y-1 text-center">
                <div className="text-lg font-semibold">{title}</div>
                <div className="text-base">{description}</div>
              </div>
              <div className="relative aspect-288/137 w-full">
                {imageSrc && (
                  <Image
                    alt={title}
                    className="object-contain"
                    src={imageSrc}
                    {...utils.imageProps.getImageProps(imageDimensions)}
                  />
                )}
                {imageIcon ?? null}
              </div>
            </div>
          ),
        )}
      </div>

      <div className="laptop:flex desktop:flex-row-reverse desktop:items-center hidden w-full flex-col gap-3">
        <div className="desktop:h-[390px] relative flex h-[420px] w-full items-center justify-center overflow-hidden rounded-lg border border-amber-300 shadow-[0_2px_4px_-2px_rgba(0,0,0,0.06),0_4px_8px_-2px_rgba(0,0,0,0.1)]">
          {selected.imageSrc && (
            <Image
              alt={selected.title}
              className="object-cover"
              src={selected.imageSrc}
              {...utils.imageProps.getImageProps(selected.imageDimensions)}
            />
          )}
          {selected.imageIcon ?? null}
        </div>
        <div className="desktop:flex-col desktop:max-w-[330px] flex w-full justify-center gap-4">
          {items.map((item) => {
            const isSelected = item.title === selectedItem;
            return (
              <div
                key={item.title}
                onClick={() => setSelectedItem(item.title)}
                className={`desktop:gap-y-2 flex cursor-pointer flex-col items-start rounded-2xl border border-amber-300 px-6 py-4 ${isSelected ? 'laptop:max-w-[316px] desktop:max-w-full bg-white text-zinc-950' : 'desktop:max-w-full laptop:max-w-[172px] bg-amber-200 text-amber-600 hover:bg-amber-50'} `}
              >
                <div className="desktop:flex-row desktop:items-center flex flex-col gap-4">
                  <div
                    className={`relative flex size-8 items-center justify-center rounded-sm border ${isSelected ? 'border-blue-600 bg-blue-500 text-white [&_img]:brightness-0 [&_img]:invert' : 'border-amber-300 bg-white text-amber-400'}`}
                  >
                    {item.iconUrl ? (
                      <>
                        {item.icon && (
                          <div
                            className={isSelected ? '' : 'absolute opacity-0'}
                          >
                            {item.icon}
                          </div>
                        )}
                        {!isSelected && (
                          <div
                            className="size-5 shrink-0"
                            style={{
                              backgroundColor: '#b7bba5',
                              WebkitMask: `url(/api/proxy-image?url=${encodeURIComponent(item.iconUrl)}) no-repeat center/contain`,
                              mask: `url(/api/proxy-image?url=${encodeURIComponent(item.iconUrl)}) no-repeat center/contain`,
                            }}
                          />
                        )}
                      </>
                    ) : (
                      item.icon
                    )}
                  </div>
                  <div className="desktop:max-w-[234px] font-semibold">
                    {item.title}
                  </div>
                </div>
                {isSelected && (
                  <div className="text-base">{item.description}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};
